import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, Users, BookOpen, Clock, Award, Download, Trophy, Eye,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useRoleData } from '../../hooks/useRoleData';
import './Analytics.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="analytics__tooltip">
        <p className="analytics__tooltip-label">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }}>
            {p.name}: <strong>{p.value}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics = () => {
  const { filteredAnalytics, filteredUsers, filteredModules, currentUser } = useRoleData();
  const { monthlyActivity = [], moduleCompletion = [], contentCategories = [], stats = { totalContent: 0, activeModules: 0, enrolledUsers: 0, avgCompletionRate: 0, totalSessions: 0, totalCompletions: 0 } } = filteredAnalytics;
  const navigate = useNavigate();
  const isTrainee = currentUser?.role === 'Trainee';

  const trainees = filteredUsers.filter((u) => u.role === 'Trainee');
  const hasActivity = monthlyActivity.some((m) => (m.sessions || 0) > 0 || (m.completions || 0) > 0);

  const handleExportReport = () => {
    const escapeCsv = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Module', 'Category', 'Level', 'Status', 'Enrolled Trainees', 'Duration (min)', 'Completion Rate (%)'],
      ...filteredModules.map((m) => [
        m.title, m.category, m.level, m.status, m.traineeIds.length, m.duration, m.completionRate,
      ]),
    ];
    const csv = rows.map((r) => r.map(escapeCsv).join(',')).join('\r\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `training-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="analytics">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>
            {currentUser?.role === 'Instructor' ? 'Module Analytics' :
             currentUser?.role === 'Trainee' ? 'My Progress' :
             'Analytics & Reports'}
          </h1>
          <p>
            {currentUser?.role === 'Instructor' ? 'Performance metrics for your assigned modules' :
             currentUser?.role === 'Trainee' ? 'Track your training progress and completion' :
             'Training performance metrics and progress overview'}
          </p>
        </div>
        <div className="analytics__header-actions">
          {currentUser?.role !== 'Trainee' && (
            <button className="btn btn-outline" onClick={() => navigate('/analytics/leaderboard')}>
              <Trophy size={14} />
              Leaderboard
            </button>
          )}
          {currentUser?.role !== 'Trainee' && (
            <button className="btn btn-outline" onClick={handleExportReport}>
              <Download size={14} />
              Export Report
            </button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className={`analytics__stats ${isTrainee ? 'analytics__stats--trainee' : ''}`}>
        {[
          { icon: Clock, label: 'Total Sessions', value: (stats?.totalSessions ?? 0).toLocaleString(), color: 'green' },
          { icon: Award, label: 'Completions', value: (stats?.totalCompletions ?? 0).toLocaleString(), color: 'dark' },
          ...(currentUser?.role !== 'Trainee' ? [
            { icon: Users, label: 'Active Trainees', value: trainees.filter((u) => u.status === 'Active').length, color: 'blue' },
          ] : []),
          { icon: TrendingUp, label: 'Avg. Completion', value: `${stats?.avgCompletionRate ?? 0}%`, color: 'yellow' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className={`analytics__stat-card analytics__stat-card--${color}`}>
            <div className="analytics__stat-icon">
              <Icon size={20} />
            </div>
            <div>
              <span className="analytics__stat-value">{value}</span>
              <span className="analytics__stat-label">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="analytics__charts-grid">
        {/* Activity Chart */}
        <div className="card analytics__chart-card analytics__chart-card--wide">
          <div className="card-header">
            <div>
              <h3>Training Activity</h3>
              <p className="text-sm text-muted">Monthly sessions vs completions</p>
            </div>
          </div>
          <div className="card-body analytics__chart-body">
            {!hasActivity && (
              <div className="empty-state" style={{ padding: 'var(--space-4)' }}>
                <Clock size={28} strokeWidth={1.5} />
                <p>
                  No VR sessions recorded yet. This chart fills in automatically once{' '}
                  {isTrainee ? 'you start training' : 'trainees start training'} in the VR headset.
                </p>
              </div>
            )}
            {hasActivity && (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthlyActivity} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  stroke="#113696"
                  strokeWidth={2.5}
                  dot={{ fill: '#113696', r: 4 }}
                  name="Sessions"
                />
                <Line
                  type="monotone"
                  dataKey="completions"
                  stroke="#a595c7"
                  strokeWidth={2.5}
                  dot={{ fill: '#a595c7', r: 4 }}
                  name="Completions"
                />
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Category Pie */}
        <div className="card analytics__chart-card">
          <div className="card-header">
            <h3>Content Categories</h3>
          </div>
          <div className="card-body analytics__chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={contentCategories}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {contentCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Share']}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Module Completion Bar */}
        <div className="card analytics__chart-card analytics__chart-card--wide">
          <div className="card-header">
            <h3>Module Completion Rates</h3>
            <p className="text-sm text-muted">% of enrolled trainees who completed each module</p>
          </div>
          <div className="card-body analytics__chart-body">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={moduleCompletion} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  interval={0}
                  angle={-10}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} formatter={(v) => [`${v}%`, 'Completion']} />
                <Bar dataKey="rate" name="Completion %" radius={[6, 6, 0, 0]}>
                  {moduleCompletion.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.rate >= 75 ? '#a595c7' : entry.rate >= 50 ? '#d1dc24' : '#2d1b69'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Trainee Progress */}
        <div className="card analytics__chart-card">
          <div className="card-header">
            <h3>
              {currentUser?.role === 'Trainee' ? 'My Progress' : 'Trainee Progress'}
            </h3>
            <span className="badge badge-dark">{trainees.length} {currentUser?.role === 'Trainee' ? 'trainee' : 'trainees'}</span>
          </div>
          <div className="card-body analytics__progress-list">
            {trainees.slice(0, 8).map((u) => (
              <div
                key={u.id}
                className="analytics__trainee-row analytics__trainee-row--clickable"
                onClick={() => !isTrainee && u.username && navigate(`/analytics/trainee/${encodeURIComponent(u.username)}`)}
                style={!isTrainee && u.username ? { cursor: 'pointer' } : {}}
              >
                <div className="avatar" style={{ width: 32, height: 32, fontSize: '0.7rem', flexShrink: 0 }}>
                  {u.avatar ? (
                    <img src={u.avatar} alt={u.name} />
                  ) : (
                    u.initials
                  )}
                </div>
                <div className="analytics__trainee-info">
                  <div className="analytics__trainee-name-row">
                    <span className="analytics__trainee-name">{u.name}</span>
                    {u.username && (
                      <span className="analytics__trainee-username">@{u.username}</span>
                    )}
                  </div>
                  <div className="analytics__trainee-progress">
                    <div className="progress-bar" style={{ height: 6 }}>
                      <div className="progress-bar-fill" style={{ width: `${u.progress}%` }} />
                    </div>
                  </div>
                </div>
                <span className="analytics__trainee-pct">{u.progress}%</span>
                {!isTrainee && (
                  <Eye size={14} className="analytics__trainee-view-icon" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Module Summary Table */}
      <div className="card">
        <div className="card-header">
          <h3>
            {currentUser?.role === 'Instructor' ? 'My Modules Performance' :
             currentUser?.role === 'Trainee' ? 'My Modules Progress' :
             'Module Performance Summary'}
          </h3>
        </div>
        <div className="analytics__table">
          <div className={`analytics__table-header ${isTrainee ? 'analytics__table-header--trainee' : ''}`}>
            <span>Module</span>
            <span>Category</span>
            {currentUser?.role !== 'Trainee' && <span>Enrolled</span>}
            <span>Duration</span>
            {currentUser?.role !== 'Trainee' && <span>Completion</span>}
            <span>Status</span>
          </div>
          {filteredModules.length === 0 && (
            <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
              <BookOpen size={28} strokeWidth={1.5} />
              <p>
                {isTrainee
                  ? 'You are not enrolled in any modules yet.'
                  : 'No modules yet — create one from the Modules page to see performance here.'}
              </p>
            </div>
          )}
          {filteredModules.map((m) => (
            <div
              key={m.id}
              className={`analytics__table-row ${isTrainee ? 'analytics__table-row--trainee' : 'analytics__table-row--clickable'}`}
              onClick={() => !isTrainee && navigate(`/analytics/module/${m.moduleId ? m.moduleId.replace(/-/g, '') : m.id}`)}
              role={!isTrainee ? 'button' : undefined}
              tabIndex={!isTrainee ? 0 : undefined}
              onKeyDown={(e) => { if (!isTrainee && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(`/analytics/module/${m.moduleId ? m.moduleId.replace(/-/g, '') : m.id}`); } }}
              style={!isTrainee ? { cursor: 'pointer' } : undefined}
            >
              <div className="analytics__table-module">
                <div className="analytics__table-icon">
                  <BookOpen size={14} />
                </div>
                <span>{m.title}</span>
              </div>
              <span className="badge badge-dark">{m.category}</span>
              {currentUser?.role !== 'Trainee' && (
                <span className="analytics__table-cell">{m.traineeIds.length} trainees</span>
              )}
              <span className="analytics__table-cell">{m.duration} min</span>
              {currentUser?.role !== 'Trainee' && (
                <div className="analytics__table-progress">
                  <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                    <div className="progress-bar-fill" style={{ width: `${m.completionRate}%` }} />
                  </div>
                  <span
                    className={`analytics__table-pct ${
                      m.completionRate >= 75 ? 'analytics__table-pct--high' :
                      m.completionRate >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                    }`}
                  >
                    {m.completionRate}%
                  </span>
                </div>
              )}
              <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                {m.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
